import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, formValueSelector, change } from 'redux-form';
import { compose } from 'react-apollo';
import { connect } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { injectIntl, FormattedMessage } from 'react-intl';
import {
  Image,
  Type,
  Upload,
  ImageIcon,
  Save,
  Loader2
} from 'lucide-react';

import ImageUploadComponent from '../ImageUploadComponent/ImageUploadComponent';
import MultiLanguageInput from '../MultiLanguageInput';

import submit from './submit';
import validate from './validate';
import messages from '../../../locale/messages';
import { photosShow } from '../../../helpers/photosShow';
import { toWebp } from '../../../helpers/imageHelper';
import { startBannerUploaderLoader, stopBannerUploaderLoader, doUploadHomeBanner } from '../../../actions/siteadmin/manageHomepageBanner';
import { homebanneruploadDir } from '../../../config';

import s from './BannerSettingsForm.css';


class BannerSettingsForm extends Component {

  static propTypes = {
    initialValues: PropTypes.object,
    title: PropTypes.string.isRequired,
    startBannerUploaderLoader: PropTypes.any.isRequired,
    doUploadImageBanner: PropTypes.any.isRequired
  };

  success = async (file, fromServer) => {
    const { doUploadHomeBanner, image, id, startBannerUploaderLoader, stopBannerUploaderLoader } = this.props;
    let fileName = fromServer.file.filename;
    let oldImage = image || null;
    startBannerUploaderLoader();
    doUploadHomeBanner(fileName, oldImage, id);
    stopBannerUploaderLoader();
  }

  handleFieldChange = (fieldName, value) => {
    const { dispatch } = this.props;
    dispatch(change('BannerSettingsForm', fieldName, value));
  };

  render() {
    const { error, handleSubmit, submitting, bannerUploaderLoading, image, formValues } = this.props;
    const { formatMessage } = this.props.intl;
    let path = photosShow(homebanneruploadDir);

    return (
      <div className={cx(s.pageContainer, 'adminPhotoUplod', 'dzInputContainer')}>
        {/* Page Header */}
        <div className={s.pageHeader}>
          <h1 className={s.pageTitle}>
            <div className={s.pageTitleIcon}>
              <Image size={24} />
            </div>
            <FormattedMessage {...messages.bannerCaptionLabel} />
          </h1>
          <p className={s.pageSubtitle}>
            Customize the hero banner that appears on your homepage
          </p>
        </div>

        <form onSubmit={handleSubmit(submit)}>
          {error && <div className="alert alert-danger">{formatMessage(error)}</div>}

          {/* Split View Layout */}
          <div className={s.splitView}>
            {/* Left: Image Preview Card */}
            <div className={s.imageCard}>
              <div className={s.imageCardHeader}>
                <div className={s.imageCardHeaderIcon}>
                  <ImageIcon size={18} />
                </div>
                <h3 className={s.imageCardTitle}>
                  <FormattedMessage {...messages.homeBannerImage} />
                </h3>
              </div>
              <div className={s.imagePreviewArea}>
                {/* Current Image Preview */}
                <div className={s.imagePreview}>
                  {image ? (
                    bannerUploaderLoading ? (
                      <div className={s.imagePreviewPlaceholder}>
                        <Loader2 size={32} className="spin" />
                        <p className={s.imagePreviewPlaceholderText}>Uploading...</p>
                      </div>
                    ) : (
                      <img src={path + toWebp(image)} alt="Banner" />
                    )
                  ) : (
                    <div className={s.imagePreviewPlaceholder}>
                      <ImageIcon size={48} className={s.imagePreviewPlaceholderIcon} />
                      <p className={s.imagePreviewPlaceholderText}>No image uploaded</p>
                    </div>
                  )}
                </div>

                {/* Upload Area */}
                <div className={s.uploadArea}>
                  <ImageUploadComponent
                    defaultMessage={formatMessage(messages.photosPlaceholder)}
                    componentConfig={{
                      iconFiletypes: ['.jpg', '.png', '.jpeg'],
                      multiple: false,
                      showFiletypeIcon: false,
                      postUrl: '/uploadHomeBanner'
                    }}
                    loaderName={'bannerUploaderLoading'}
                    success={this.success}
                  >
                    <Upload size={24} className={s.uploadAreaIcon} />
                    <p className={s.uploadAreaText}>
                      {image ? 'Click to change image' : 'Click to upload image'}
                    </p>
                    <p className={s.uploadAreaHint}>JPG, PNG up to 5MB</p>
                  </ImageUploadComponent>
                </div>
              </div>
            </div>

            {/* Right: Form Card */}
            <div className={s.formCard}>
              <div className={s.formCardHeader}>
                <div className={s.formCardHeaderIcon}>
                  <Type size={18} />
                </div>
                <h3 className={s.formCardTitle}>Banner Content</h3>
              </div>
              <div className={s.formCardBody}>
                {/* Title - Multi-language */}
                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    <FormattedMessage {...messages.titleAdminLabel} />
                  </label>
                  <MultiLanguageInput
                    baseField="title"
                    values={formValues}
                    onChange={this.handleFieldChange}
                    inputType="text"
                    placeholder={formatMessage(messages.bannerPlaceholder)}
                  />
                </div>

                {/* Content - Multi-language */}
                <div className={s.formGroup}>
                  <label className={s.formLabel}>
                    <FormattedMessage {...messages.contentLabel} />
                  </label>
                  <MultiLanguageInput
                    baseField="content"
                    values={formValues}
                    onChange={this.handleFieldChange}
                    inputType="textarea"
                    placeholder={formatMessage(messages.bannerPlaceholderContent)}
                  />
                </div>
              </div>

              {/* Footer with Save Button */}
              <div className={s.formCardFooter}>
                <button type="submit" className={s.saveButton} disabled={submitting}>
                  {submitting ? <Loader2 size={18} className="spin" /> : <Save size={18} />}
                  <FormattedMessage {...messages.save} />
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    );
  }

}

BannerSettingsForm = reduxForm({
  form: 'BannerSettingsForm', // a unique name for this form
  validate,
  enableReinitialize: true,
  keepDirtyOnReinitialize: false
})(BannerSettingsForm);

const selector = formValueSelector('BannerSettingsForm');

const getMultiLangFields = (state, baseFields) => {
  const result = {};
  const langs = ['', '_en', '_fr', '_it', '_pt'];
  baseFields.forEach(field => {
    langs.forEach(lang => {
      const fieldName = `${field}${lang}`;
      result[fieldName] = selector(state, fieldName);
    });
  });
  return result;
};

const mapState = (state) => ({
  bannerUploaderLoading: state.loader.bannerUploaderLoading,
  formValues: getMultiLangFields(state, ['title', 'content'])
});

const mapDispatch = {
  doUploadHomeBanner,
  startBannerUploaderLoader,
  stopBannerUploaderLoader,
  change
};

export default compose(
  injectIntl,
  withStyles(s),
  connect(mapState, mapDispatch)
)(BannerSettingsForm);